<script setup>
import PlayerMinutesSettings from './PlayerMinutesSettings.vue'
import DepthChartColumns from './DepthChartColumns.vue'
import DepthChartTable from './DepthChartTable.vue'
import PlayerLabelProjectionWeights from './PlayerLabelProjectionWeights.vue'
import LeaguesOpponentMatchUps from './LeaguesOpponentMatchUps.vue'
// import Per30TemplatesComponent from '../Per30TemplatesComponent.vue'
import { useProjectionsStore } from '../stores/projections'
import { ref, computed, onMounted, onUnmounted, watch } from 'vue'
import axios from 'axios'

const projectionsStore = useProjectionsStore()

// Per30Templates component state (inline)
const per30Loading = ref(false)
const per30Error = ref(null)
const per30Templates = ref(null)

// Auto projections loading state
const autoProjectionsLoading = ref(false)

// Refs for depth chart components
const homeDepthChartRef = ref(null)
const awayDepthChartRef = ref(null)

// Get team data from projectionsStore
const homeTeam = computed(() => projectionsStore.projectionsData?.home_team)
const awayTeam = computed(() => projectionsStore.projectionsData?.away_team)
const homeTeamId = computed(() => homeTeam.value?.id || homeTeam.value?.team_id)
const awayTeamId = computed(() => awayTeam.value?.id || awayTeam.value?.team_id)

// Per30Templates computed properties (inline)
const groupedPer30Templates = computed(() => {
  if (!per30Templates.value || !Array.isArray(per30Templates.value)) {
    return {}
  }
  
  return per30Templates.value.reduce((acc, template) => {
    const playerId = template.player_id
    if (!acc[playerId]) {
      acc[playerId] = {
        player_id: playerId,
        player_name: template.player_name || template.player?.player_name || template.player?.name || `Player ${playerId}`,
        player: template.player,
        templates: {}
      }
    }
    
    acc[playerId].templates[template.template_name] = template
    return acc
  }, {})
})

const uniquePer30Templates = computed(() => {
  if (!per30Templates.value || !Array.isArray(per30Templates.value)) {
    return []
  }
  
  const templates = new Set()
  per30Templates.value.forEach(template => {
    if (template.template_name) {
      templates.add(template.template_name)
    }
  })
  
  return Array.from(templates)
})

// Per30Templates fetch function (inline)
const fetchPer30Templates = async () => {
  if (!props.fixtureId) {
    per30Error.value = 'Fixture ID is required'
    return
  }

  per30Loading.value = true
  per30Error.value = null

  try {
    const response = await axios.post('api/v2/projections-settings/projection-per30-templates', {
      fixture_id: props.fixtureId
    })
    
    per30Templates.value = response.data
  } catch (err) {
    per30Error.value = err.response?.data?.message || err.message || 'Failed to fetch per-30 templates'
    console.error('Error fetching per-30 templates:', err)
  } finally {
    per30Loading.value = false
  }
}

// Fetch per-30 templates on mount
onMounted(() => {
  fetchPer30Templates()
})

const props = defineProps({
  show: {
    type: Boolean,
    required: true
  },
  leagueId: {
    type: [String, Number],
    required: true
  },
  teamId: {
    type: [String, Number],
    required: true
  },
  fixtureId: {
    type: [String, Number],
    required: true
  }
})

const emit = defineEmits(['close'])

// Close modal function
const closeModal = () => {
  emit('close')
}

// Close on Escape key
const handleKeydown = (event) => {
  if (event.key === 'Escape') {
    closeModal()
  }
}

// Body scroll locking
const lockBodyScroll = () => {
  document.body.classList.add('modal-open')
}

const unlockBodyScroll = () => {
  document.body.classList.remove('modal-open')
}
const createAutoProjections = async () => {
  autoProjectionsLoading.value = true
  try {
    // Collect temporary depth chart data from both teams
    const depthChartOverrides = {}
    
    if (homeDepthChartRef.value?.depthChartData) {
      depthChartOverrides.home = homeDepthChartRef.value.depthChartData
    }
    
    if (awayDepthChartRef.value?.depthChartData) {
      depthChartOverrides.away = awayDepthChartRef.value.depthChartData
    }
    
    const payload = {
      fixture_id: props.fixtureId
    }
    
    // Only include depth chart overrides if we have data
    if (Object.keys(depthChartOverrides).length > 0) {
      payload.depth_chart_overrides = depthChartOverrides
    }
    
    await axios.post('/api/v2/projections-settings/create-auto-projections', payload)
    // Optionally, show a success message or close modal
  } catch (err) {
    // Optionally, handle error (e.g., show notification)
    console.error('Failed to create auto projections:', err)
  } finally {
    autoProjectionsLoading.value = false
  }
}


// Watch for show prop changes to lock/unlock body scroll
watch(() => props.show, (newValue) => {
  if (newValue) {
    lockBodyScroll()
  } else {
    unlockBodyScroll()
  }
}, { immediate: true })

// Add/remove event listener for escape key
onMounted(() => {
  document.addEventListener('keydown', handleKeydown)
})

// Cleanup event listener
onUnmounted(() => {
  document.removeEventListener('keydown', handleKeydown)
  unlockBodyScroll() // Ensure body scroll is unlocked when component unmounts
})
</script>

<template>
  <!-- Modal Overlay -->
  <div 
    v-if="show" 
    class="fixed inset-0 z-50 overflow-y-auto modal-overlay"
    @click.self="closeModal"
  >
    <!-- Backdrop -->
    <div class="fixed inset-0 bg-black bg-opacity-50 transition-opacity"></div>
    
    <!-- Modal Container -->
    <div class="flex min-h-full items-center justify-center p-4">
      <div class="relative w-full max-w-[95vw] max-h-[95vh] bg-white rounded-lg shadow-xl border transform transition-all">
        <!-- Header -->
        <div class="flex items-center justify-between p-6 border-b bg-gray-50 rounded-t-lg">
          <div class="flex items-center gap-3">
            <svg class="w-6 h-6 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"></path>
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"></path>
            </svg>
            <h3 class="text-2xl font-bold text-gray-800">Automatic Pre-Projections Settings</h3>
          </div>
          <button 
            @click.stop="closeModal"
            class="p-2 hover:bg-gray-200 rounded-full transition-colors"
            title="Close (ESC)"
          >
            <svg class="w-6 h-6 text-gray-500 hover:text-gray-700" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M6 18L18 6M6 6l12 12"></path>
            </svg>
          </button>
        </div>

        <!-- Content -->
        <div class="p-6 space-y-8 max-h-[75vh] overflow-y-auto modal-content">
      <!-- Depth Chart Tables Section -->
      <div>
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-5 h-5 text-teal-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h14a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1z"></path>
          </svg>
          <h4 class="text-lg font-semibold text-gray-800">Depth Chart Tables</h4>
        </div>
        
        <!-- Two Team Depth Charts -->
        <div class="space-y-6">
          <!-- Home Team Depth Chart -->
          <div v-if="homeTeamId" class="space-y-2">
            <div class="flex items-center gap-2 mb-2">
              <h5 class="text-md font-semibold text-gray-700">
                {{ homeTeam?.team_name || 'Home Team' }}
              </h5>
              <span class="text-xs text-gray-500">(Home)</span>
            </div>
            <DepthChartTable 
              :team-id="homeTeamId"
              ref="homeDepthChartRef"
            />
          </div>
          
          <!-- Away Team Depth Chart -->
          <div v-if="awayTeamId" class="space-y-2">
            <div class="flex items-center gap-2 mb-2">
              <h5 class="text-md font-semibold text-gray-700">
                {{ awayTeam?.team_name || 'Away Team' }}
              </h5>
              <span class="text-xs text-gray-500">(Away)</span>
            </div>
            <DepthChartTable 
              :team-id="awayTeamId"
              ref="awayDepthChartRef"
            />
          </div>
          
          <!-- No Teams Available -->
          <div v-if="!homeTeamId && !awayTeamId" class="text-center py-8 text-gray-500">
            <i class="fas fa-exclamation-triangle text-2xl mb-2"></i>
            <p>No team data available for this fixture</p>
          </div>
        </div>
      </div>

      <!-- Player Minutes Settings Section -->
      <div>
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-5 h-5 text-blue-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z"></path>
          </svg>
          <h4 class="text-lg font-semibold text-gray-800">Player Minutes Settings</h4>
        </div>
        <PlayerMinutesSettings 
          :league-id="leagueId" 
          :team-id="teamId" 
          :fixture-id="fixtureId"
          :players="projectionsStore.mergedTeams?.map(team => team.players).flat() || []"
        />
      </div>

      <!-- Depth Chart Columns Section -->
      <div>
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-5 h-5 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 10h18M3 14h18m-9-4v8m-7 0V4a1 1 0 011-1h14a1 1 0 011 1v16a1 1 0 01-1 1H4a1 1 0 01-1-1z"></path>
          </svg>
          <h4 class="text-lg font-semibold text-gray-800">Depth Chart Columns</h4>
        </div>
        <DepthChartColumns 
          :league-id="leagueId" 
          :team-id="teamId" 
        />
      </div>
<!-- getProjectionPer30Templates -->

      <!-- Player Label Projection Weights Section -->
      <div>
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-5 h-5 text-purple-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M3 6l3 1m0 0l-3 9a5.002 5.002 0 006.001 0M6 7l3 9M6 7l6-2m6 2l3-1m-3 1l-3 9a5.002 5.002 0 006.001 0M18 7l3 9m-3-9l-6-2m0-2v2m0 16V5m0 16H9m3 0h3"></path>
          </svg>
          <h4 class="text-lg font-semibold text-gray-800">Player Label Projection Weights</h4>
        </div>
        <PlayerLabelProjectionWeights 
          :league-id="leagueId" 
          :team-id="teamId" 
        />
      </div>

      <!-- Leagues Opponent Match-ups Section -->
      <div>
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-5 h-5 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z"></path>
          </svg>
          <h4 class="text-lg font-semibold text-gray-800">Leagues Opponent Match-ups</h4>
        </div>
        <LeaguesOpponentMatchUps 
          :league-id="leagueId" 
          :team-id="teamId" 
        />
      </div>

      <!-- Projection Per-30 Templates Section -->
      <div>
        <div class="flex items-center gap-3 mb-4">
          <svg class="w-5 h-5 text-indigo-500" fill="none" stroke="currentColor" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 5H7a2 2 0 00-2 2v10a2 2 0 002 2h8a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2m-3 7h3m-3 4h3m-6-4h.01M9 16h.01"></path>
          </svg>
          <h4 class="text-lg font-semibold text-gray-800">Projection Per-30 Templates</h4>
        </div>
        <!-- Per-30 Templates Content (inline) -->
        <div class="space-y-4">
          <!-- Loading State -->
          <div v-if="per30Loading" class="flex items-center justify-center py-8">
            <div class="flex items-center gap-3">
              <i class="fas fa-spinner fa-spin text-blue-500"></i>
              <span class="text-gray-600">Loading per-30 templates...</span>
            </div>
          </div>

          <!-- Error State -->
          <div v-else-if="per30Error" class="bg-red-50 border border-red-200 rounded-lg p-4">
            <div class="flex items-center gap-3">
              <i class="fas fa-exclamation-triangle text-red-500"></i>
              <div>
                <h4 class="text-red-800 font-medium">Error Loading Templates</h4>
                <p class="text-red-600 text-sm mt-1">{{ per30Error }}</p>
              </div>
            </div>
            <button 
              @click="fetchPer30Templates" 
              class="mt-3 px-4 py-2 bg-red-600 text-white rounded hover:bg-red-700 transition-colors"
            >
              <i class="fas fa-redo mr-2"></i>
              Retry
            </button>
          </div>

          <!-- Success State -->
          <div v-else-if="per30Templates" class="space-y-4">
            <div class="bg-green-50 border border-green-200 rounded-lg p-4">
              <div class="flex items-center gap-3">
                <i class="fas fa-check-circle text-green-500"></i>
                <div>
                  <h4 class="text-green-800 font-medium">Per-30 Templates Loaded</h4>
                  <p class="text-green-600 text-sm mt-1">
                    Found {{ Object.keys(groupedPer30Templates).length }} players with {{ uniquePer30Templates.length }} templates
                  </p>
                </div>
              </div>
            </div>

            <!-- Per-30 Templates Table -->
            <div class="bg-white rounded-lg border overflow-hidden">
              <div class="overflow-x-auto">
                <table class="min-w-full divide-y divide-gray-200">
                  <thead class="bg-gray-50">
                    <tr>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Player</th>
                      <th class="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Template</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Points</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Rebounds</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Assists</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">3PT</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Turnovers</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Steals</th>
                      <th class="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">Blocks</th>
                    </tr>
                  </thead>
                  <tbody class="bg-white divide-y divide-gray-200">
                    <template v-for="(playerData, playerId) in groupedPer30Templates" :key="playerId">
                      <tr v-for="(template, templateName) in playerData.templates" :key="`${playerId}-${templateName}`" class="hover:bg-gray-50">
                        <td v-if="templateName === Object.keys(playerData.templates)[0]" 
                            :rowspan="Object.keys(playerData.templates).length"
                            class="px-4 py-3 whitespace-nowrap bg-gray-50">
                          <div class="text-sm font-medium text-gray-900">{{ playerData.player_name }}</div>
                          <div class="text-xs text-gray-500">ID: {{ playerData.player_id }}</div>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap">
                          <div class="text-sm font-medium text-gray-900">{{ template.template_label }}</div>
                          <div class="text-xs text-gray-500">{{ template.template_name }}</div>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.points || '-' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.total_rebounds || '-' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.assists || '-' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.three_points_field_goal || '-' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.turnovers || '-' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.steals || '-' }}</span>
                        </td>
                        <td class="px-4 py-3 whitespace-nowrap text-center">
                          <span class="text-sm font-medium text-gray-900">{{ template.blocks || '-' }}</span>
                        </td>
                      </tr>
                    </template>
                  </tbody>
                </table>
              </div>
            </div>
          </div>

          <!-- Empty State -->
          <div v-else class="text-center py-8">
            <i class="fas fa-info-circle text-gray-400 text-4xl mb-4"></i>
            <p class="text-gray-600">No per-30 templates data available</p>
          </div>
        </div>
      </div>
        </div>
        
        <!-- Footer -->
        <div class="flex items-center justify-end gap-3 p-6 border-t bg-gray-50 rounded-b-lg">
          <button 
            @click.stop="closeModal"
            class="px-6 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
          >
            Close
          </button>
          <button 
            class="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Save Settings
          </button>
          <button
            class="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            @click="createAutoProjections"
            :disabled="autoProjectionsLoading"
          >
            <i v-if="autoProjectionsLoading" class="fas fa-spinner fa-spin mr-2"></i>
            {{ autoProjectionsLoading ? 'Creating...' : 'Create Autoprojections' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Loading Popup Overlay -->
    <div 
      v-if="autoProjectionsLoading" 
      class="fixed inset-0 z-60 flex items-center justify-center bg-black bg-opacity-60"
    >
      <div class="bg-white rounded-lg shadow-2xl p-8 max-w-md w-full mx-4">
        <div class="text-center">
          <div class="mb-6">
            <i class="fas fa-spinner fa-spin text-4xl text-blue-500"></i>
          </div>
          <h3 class="text-xl font-semibold text-gray-800 mb-2">Creating Auto Projections</h3>
          <p class="text-gray-600 mb-4">Please wait while we generate your automatic projections...</p>
          <div class="w-full bg-gray-200 rounded-full h-2">
            <div class="bg-blue-500 h-2 rounded-full animate-pulse" style="width: 100%"></div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
/* Modal overlay and backdrop */
.modal-overlay {
  backdrop-filter: blur(2px);
}

/* Ensure modal content is scrollable */
.modal-content {
  scrollbar-width: thin;
  scrollbar-color: #cbd5e0 #f7fafc;
}

.modal-content::-webkit-scrollbar {
  width: 6px;
}

.modal-content::-webkit-scrollbar-track {
  background: #f7fafc;
}

.modal-content::-webkit-scrollbar-thumb {
  background: #cbd5e0;
  border-radius: 3px;
}

.modal-content::-webkit-scrollbar-thumb:hover {
  background: #a0aec0;
}

/* Animation for modal appearance */
.modal-enter-active, .modal-leave-active {
  transition: all 0.3s ease;
}

.modal-enter-from, .modal-leave-to {
  opacity: 0;
  transform: scale(0.95);
}

/* Prevent body scroll when modal is open */
body.modal-open {
  overflow: hidden;
}
</style>
